import { QueryClientContext } from '@tanstack/react-query';
import { Heart } from 'lucide-react';
import { useContext, useState, type MouseEvent } from 'react';

import { useShopContent } from '@/features/storefront/content/useContent';
import { useWishlist, useWishlistPlugin } from '@/features/storefront/wishlist/useWishlist';
import { cn } from '@/lib/cn';

/**
 * The heart: save a product to the wishlist, or take it off.
 *
 * A toggle button (`aria-pressed`) whose name says what a tap will do. It
 * fills the moment it is tapped; if the server then refuses, it empties again
 * and says so to screen readers.
 *
 * Safe inside a card's link: the click neither follows the link nor bubbles
 * to it.
 */
export function WishlistButton({
  productId,
  variant = 'card',
  className,
}: {
  productId: string;
  /** `card`: a small round button over a picture. `product`: beside add-to-cart. */
  variant?: 'card' | 'product';
  className?: string;
}) {
  const text = useShopContent();
  const wishlist = useWishlist();
  const [failed, setFailed] = useState(false);
  const saved = wishlist.has(productId);
  const label = saved ? text('wishlist.remove') : text('wishlist.add');

  const onClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setFailed(false);
    wishlist.toggle(productId).catch(() => setFailed(true));
  };

  return (
    <>
      <button
        type="button"
        aria-pressed={saved}
        aria-label={label}
        title={label}
        onClick={onClick}
        data-wishlist-button={variant}
        className={cn(
          'inline-flex shrink-0 items-center justify-center transition-transform active:scale-90',
          variant === 'card'
            ? 'size-9 rounded-full shadow-sm'
            : 'size-11 rounded-[var(--storefront-radius)] border',
          className,
        )}
        style={{
          backgroundColor: 'var(--storefront-background)',
          borderColor: 'var(--storefront-surface)',
          color: saved ? 'var(--storefront-primary)' : 'var(--storefront-text)',
        }}
      >
        <Heart className={variant === 'card' ? 'size-4' : 'size-5'} fill={saved ? 'currentColor' : 'none'} aria-hidden />
      </button>
      {failed ? (
        <span role="status" className="sr-only">
          {text('wishlist.error')}
        </span>
      ) : null}
    </>
  );
}

/**
 * A product card's heart, when the shop's wishlist plugin puts one there.
 *
 * Nothing at all where there is no query client (a card drawn on its own, as
 * some tests and static thumbnails do), so a card never needs the wishlist's
 * providers to render.
 */
export function CardWishlistButton({ productId, className }: { productId: string; className?: string }) {
  const client = useContext(QueryClientContext);
  if (!client) return null;
  return <CardWishlistButtonInner productId={productId} className={className} />;
}

function CardWishlistButtonInner({ productId, className }: { productId: string; className?: string }) {
  const plugin = useWishlistPlugin();
  if (!plugin.on || !plugin.show_on_cards) return null;
  return <WishlistButton productId={productId} variant="card" className={className} />;
}

/**
 * The card's heart in the picture's top-right corner - the card's actions
 * slot, for a card whose caller passed no actions of its own. Nothing at all
 * (no wrapper either) while the plugin leaves cards alone.
 */
export function CardWishlistSlot({ productId }: { productId: string }) {
  const client = useContext(QueryClientContext);
  if (!client) return null;
  return <CardWishlistSlotInner productId={productId} />;
}

function CardWishlistSlotInner({ productId }: { productId: string }) {
  const plugin = useWishlistPlugin();
  if (!plugin.on || !plugin.show_on_cards) return null;
  return (
    <div className="absolute right-3 top-3 z-10" data-card-actions>
      <WishlistButton productId={productId} variant="card" />
    </div>
  );
}
